import React, { useState, useEffect, useMemo } from 'react';
import { salesAPI } from '../services/api';
import CreateOrderModal from '../components/CreateOrderModal';
import SaleTicket from '../components/SaleTicket';
import ActionModal from '../components/ActionModal';
import Toast from '../components/Toast';
import './OrdersManagement.css';

const OrdersManagementPage = () => {
    const [orders, setOrders] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState(null);
    const [showTicket, setShowTicket] = useState(false);
    const [ticketData, setTicketData] = useState(null);
    const [actionOrder, setActionOrder] = useState(null);
    const [showActionModal, setShowActionModal] = useState(false);
    const [toast, setToast] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const ordersPerPage = 50;
    const [totalCount, setTotalCount] = useState(0);
    const [paymentAmount, setPaymentAmount] = useState(0);
    const [newStatus, setNewStatus] = useState('');
    const [statusNotes, setStatusNotes] = useState('');
    const [newDeliveryDate, setNewDeliveryDate] = useState('');
    const [serverStats, setServerStats] = useState({});
    const [showCreateModal, setShowCreateModal] = useState(false);

    // Detectar móvil
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        loadOrders();
        loadStats();
    }, [statusFilter, currentPage]);

    useEffect(() => {
        if (toast) {
            const timer = setTimeout(() => setToast(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [toast]);

    const calculatedStats = useMemo(() => {
        if (totalCount <= ordersPerPage && orders.length > 0) {
            return orders.reduce((acc, order) => {
                const status = (order.order_status || order.status || '').toLowerCase();
                if (status === 'pendiente') acc.pending++;
                if (status === 'en_preparacion' || status === 'produccion') acc.production++;
                if (status === 'listo') acc.ready++;
                const balance = parseFloat(order.balance_due || order.balance || 0);
                if (balance > 0 && status !== 'cancelado') acc.money += balance;
                return acc;
            }, { pending: 0, production: 0, ready: 0, money: 0 });
        }
        return {
            pending: Number(serverStats.pendiente || serverStats.pending || 0),
            production: Number(serverStats.en_preparacion || serverStats.produccion || 0),
            ready: Number(serverStats.listo || 0),
            money: parseFloat(serverStats.total_por_cobrar || serverStats.total_balance || 0)
        };
    }, [orders, serverStats, totalCount]);

    const loadOrders = async () => {
        setIsLoading(true);
        try {
            const params = { page: currentPage, limit: ordersPerPage };
            if (statusFilter !== 'all') {
                params.order_status = statusFilter === 'produccion' ? 'en_preparacion' : statusFilter;
            }
            const response = await salesAPI.getOrders(params);
            const data = response.data.data;
            setOrders((data.orders || []).sort((a, b) => b.id - a.id));
            setTotalPages(data.pagination.pages);
            if (statusFilter === 'all') setTotalCount(data.pagination.total || 0);
        } catch (error) {
            console.error(error);
            setOrders([]);
        } finally {
            setIsLoading(false);
        }
    };

    const loadStats = async () => {
        try {
            const response = await salesAPI.getPendingOrdersCount();
            setServerStats(response.data.data || {});
        } catch (error) { console.error(error); }
    };

    const showToast = (message, type = 'success') => setToast({ message, type });

    const handleOpenActionModal = (e, order) => {
        e.stopPropagation();
        setActionOrder(order);
        setShowActionModal(true);
    };

    const safeDate = (dateString) => {
        if (!dateString) return '';
        try { return new Date(dateString).toISOString().split('T')[0]; }
        catch (e) { return ''; }
    };

    const openFormModal = (order, type) => {
        setShowActionModal(false);
        setSelectedOrder(order);
        setModalType(type);
        if (type === 'payment') { setPaymentAmount(0); setStatusNotes(''); }
        else if (type === 'status') {
            setNewStatus(order.order_status || order.status || 'pendiente');
            setStatusNotes('');
            setNewDeliveryDate(safeDate(order.delivery_date));
        }
        setShowModal(true);
    };

    const closeModal = () => { setShowModal(false); setSelectedOrder(null); setModalType(null); };

    const handleAddPayment = async () => {
        if (paymentAmount <= 0) return showToast('⚠️ El monto debe ser mayor a 0', 'error');
        try {
            await salesAPI.addPayment(selectedOrder.id, { amount: paymentAmount, notes: statusNotes });
            showToast('✅ Pago registrado correctamente');
            closeModal(); loadOrders(); loadStats();
        } catch (error) { showToast('❌ Error: ' + (error.response?.data?.message || error.message), 'error'); }
    };

    const handleUpdateStatus = async () => {
        try {
            await salesAPI.updateStatus(selectedOrder.id, {
                order_status: newStatus.toLowerCase(),
                delivery_notes: statusNotes,
                delivery_date: newDeliveryDate
            });
            showToast('✅ Estado actualizado');
            closeModal(); loadOrders(); loadStats();
        } catch (error) { showToast('❌ Error: ' + (error.response?.data?.message || error.message), 'error'); }
    };

    const handleDelete = async (orderId) => {
        setShowActionModal(false);
        if (!window.confirm('⚠️ ¿Eliminar permanentemente?')) return;
        try {
            await salesAPI.deleteOrder(orderId);
            showToast('🗑️ Pedido eliminado');
            loadOrders(); loadStats();
        } catch (error) { showToast('❌ Error: ' + (error.response?.data?.message || error.message), 'error'); }
    };

    const handleInvoice = async (orderId) => {
        setShowActionModal(false);
        if (!window.confirm('¿Confirmar entrega?')) return;
        try {
            await salesAPI.invoiceOrder(orderId, {});
            showToast('📦 Pedido entregado');
            loadOrders(); loadStats();
        } catch (error) { showToast('❌ Error: ' + (error.response?.data?.message || error.message), 'error'); }
    };

    const handlePrintTicket = async (orderId) => {
        setShowActionModal(false);
        try {
            const response = await salesAPI.getById(orderId);
            if (response.data.success) {
                setTicketData({ ...response.data.data.sale, items: response.data.data.items });
                setShowTicket(true);
            }
        } catch (error) { showToast('❌ Error al cargar ticket', 'error'); }
    };

    const getOrderActions = () => {
        const current = actionOrder;
        if (!current) return [];
        const status = current.order_status || current.status;
        const balance = parseFloat(current.balance_due || current.balance || 0);
        const actions = [];
        actions.push({ icon: '👁️', title: 'Ver Detalles', subtitle: 'Información completa', variant: 'view', onClick: () => openFormModal(current, 'details') });
        if (balance > 0) actions.push({ icon: '💵', title: 'Registrar Pago', subtitle: `Saldo: S/. ${balance.toFixed(2)}`, variant: 'stock', onClick: () => openFormModal(current, 'payment') });
        actions.push({ icon: '🖨️', title: 'Imprimir Ticket', subtitle: 'Generar comprobante', variant: 'default', onClick: () => handlePrintTicket(current.id) });
        if (status !== 'cancelado') actions.push({ icon: '📝', title: 'Editar / Estado', subtitle: 'Cambiar estado o fecha', variant: 'edit', onClick: () => openFormModal(current, 'status') });
        if (status === 'listo') actions.push({ icon: '📦', title: 'Entregar Pedido', subtitle: 'Marcar como entregado', variant: 'stock', onClick: () => handleInvoice(current.id) });
        if (status === 'cancelado') actions.push({ icon: '🗑️', title: 'Eliminar', subtitle: 'Borrar permanentemente', variant: 'delete', onClick: () => handleDelete(current.id) });
        return actions;
    };

    const getStatusColor = (status) => {
        const colors = {
            'pendiente': '#ffc107', 'en_preparacion': '#3b82f6',
            'produccion': '#3b82f6', 'listo': '#28a745',
            'entregado': '#6c757d', 'cancelado': '#dc3545'
        };
        return colors[status] || '#6c757d';
    };

    const getStatusLabel = (status) => status === 'en_preparacion' ? 'PRODUCCIÓN' : status?.toUpperCase();

    const filteredOrders = orders.filter(o => {
        const displayId = o.correlativo_local || o.id;
        return String(displayId).includes(searchTerm) ||
        o.customer_name?.toLowerCase().includes(searchTerm.toLowerCase());
    });

    const MobileOrderCard = ({ order }) => {
        const displayId = order.correlativo_local || order.id;
        const status = order.order_status || order.status;
        const balance = parseFloat(order.balance_due || order.balance || 0);
        return (
            <div className="order-card-mobile">
                <div className="order-card-top">
                    <span className="order-id">#{typeof displayId === 'number' ? `VTA-${String(displayId).padStart(5, '0')}` : displayId}</span>
                    <span className="status-badge" style={{ background: getStatusColor(status) + '20', color: getStatusColor(status) }}>
                        {getStatusLabel(status)}
                    </span>
                    <button className="kebab-btn" onClick={(e) => handleOpenActionModal(e, order)}>⋮</button>
                </div>
                <div className="order-card-body">
                    <div className="order-card-customer">{order.customer_name}</div>
                    {order.customer_phone && <div className="order-card-phone">📞 {order.customer_phone}</div>}
                </div>
                <div className="order-card-bottom">
                    <span>Total: <strong>S/. {parseFloat(order.total_amount || order.total || 0).toFixed(2)}</strong></span>
                    <span className={`amount-balance ${balance > 0 ? 'pending' : 'paid'}`}>
                        Saldo: S/. {balance.toFixed(2)}
                    </span>
                    {order.delivery_date && <span>📅 {safeDate(order.delivery_date)}</span>}
                </div>
            </div>
        );
    };

    return (
        <div className="content-pane" style={{ width: '100%', maxWidth: '100%' }}>
            <div className="orders-header">
                <div>
                    <h2>📋 Gestión de Pedidos</h2>
                    <p className="orders-header-subtitle">Control y seguimiento de producción</p>
                </div>
            </div>

            {/* KPIs */}
            <div className="kpi-grid">
                <div className="kpi-card total"><div className="kpi-label">TOTAL PEDIDOS</div><div className="kpi-value">{totalCount}</div></div>
                <div className="kpi-card pending"><div className="kpi-label">PENDIENTES</div><div className="kpi-value">{calculatedStats.pending}</div></div>
                <div className="kpi-card production"><div className="kpi-label">EN PRODUCCIÓN</div><div className="kpi-value">{calculatedStats.production}</div></div>
                <div className="kpi-card money">
                    <div className="kpi-label">TOTAL POR COBRAR</div>
                    <div className="kpi-value" style={{ color: '#dc3545' }}>S/. {calculatedStats.money.toFixed(2)}</div>
                </div>
            </div>

            {/* Filtros */}
            <div className="filters-container">
                {['all', 'pendiente', 'produccion', 'listo', 'entregado'].map(st => (
                    <button key={st} onClick={() => setStatusFilter(st)} className={`filter-btn ${st} ${statusFilter === st ? 'active' : ''}`}>
                        {st === 'all' ? 'Todos' : st === 'produccion' ? 'Producción' : st.charAt(0).toUpperCase() + st.slice(1)}
                    </button>
                ))}
                <button onClick={() => setShowCreateModal(true)} className="create-order-btn">➕ Nuevo</button>
                <button onClick={loadOrders} className="refresh-btn">🔄</button>
            </div>

            <div className="search-container">
                <input type="text" placeholder="🔍 Buscar pedido..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="search-input" />
            </div>

            {/* TABLA (PC) / TARJETAS (Móvil) */}
            {isLoading ? (
                <div className="loading-container"><p>⏳ Cargando...</p></div>
            ) : filteredOrders.length === 0 ? (
                <div className="empty-container"><p>No se encontraron pedidos</p></div>
            ) : isMobile ? (
                // ── VISTA MÓVIL: Tarjetas ──
                <div className="orders-cards-container">
                    {filteredOrders.map(order => (
                        <MobileOrderCard key={order.id} order={order} />
                    ))}
                </div>
            ) : (
                // ── VISTA PC: Tabla ──
                <div className="table-container">
                    <table className="orders-table">
                        <thead>
                            <tr>
                                <th>ID</th><th>Cliente</th><th className="center">Estado</th>
                                <th className="right">Total</th><th className="right">Saldo</th>
                                <th className="center">Entrega</th><th className="center">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredOrders.map(order => {
                                const displayId = order.correlativo_local || order.id;
                                const status = order.order_status || order.status;
                                const balance = parseFloat(order.balance_due || order.balance || 0);
                                return (
                                    <tr key={order.id}>
                                        <td><span className="order-id">{typeof displayId === 'number' ? `VTA-${String(displayId).padStart(5, '0')}` : displayId}</span></td>
                                        <td>
                                            <div className="customer-info">
                                                <span className="customer-name">{order.customer_name}</span>
                                                {order.customer_phone && <small>📞 {order.customer_phone}</small>}
                                            </div>
                                        </td>
                                        <td className="center">
                                            <span className="status-badge" style={{ background: getStatusColor(status) + '20', color: getStatusColor(status) }}>
                                                {getStatusLabel(status)}
                                            </span>
                                        </td>
                                        <td className="right">S/. {parseFloat(order.total_amount || order.total || 0).toFixed(2)}</td>
                                        <td className="right">
                                            <span className={`amount-balance ${balance > 0 ? 'pending' : 'paid'}`}>S/. {balance.toFixed(2)}</span>
                                        </td>
                                        <td className="center">{order.delivery_date ? safeDate(order.delivery_date) : '-'}</td>
                                        <td className="center">
                                            <div className="action-menu-wrapper">
                                                <button className="kebab-btn" onClick={(e) => handleOpenActionModal(e, order)}>⋮</button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <ActionModal
                isOpen={showActionModal}
                onClose={() => setShowActionModal(false)}
                title="Opciones de Pedido"
                itemName={`Pedido ${actionOrder ? (typeof (actionOrder.correlativo_local || actionOrder.id) === 'number' ? `VTA-${String(actionOrder.correlativo_local || actionOrder.id).padStart(5, '0')}` : (actionOrder.correlativo_local || actionOrder.id)) : ''}`}
                itemSubtitle={actionOrder?.customer_name}
                actions={getOrderActions()}
            />

            {showModal && selectedOrder && (
                <div className="modal-overlay">
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{modalType === 'payment' ? '💵 Registrar Pago' : modalType === 'status' ? '📝 Editar Pedido' : '📋 Detalles'}</h3>
                            <button className="close-btn" onClick={closeModal}>✕</button>
                        </div>
                        <div className="modal-body">
                            {modalType === 'details' && (
                                <>
                                    <p><strong>Cliente:</strong> {selectedOrder.customer_name}</p>
                                    <p><strong>Total:</strong> S/. {parseFloat(selectedOrder.total_amount || selectedOrder.total || 0).toFixed(2)}</p>
                                    <p><strong>Notas:</strong> {selectedOrder.notes || 'Sin notas'}</p>
                                    <div className="modal-actions"><button onClick={closeModal} className="modal-btn-secondary">Cerrar</button></div>
                                </>
                            )}
                            {modalType === 'payment' && (
                                <>
                                    <div className="modal-field">Saldo pendiente: <b>S/. {parseFloat(selectedOrder.balance_due || selectedOrder.balance || 0).toFixed(2)}</b></div>
                                    <input type="number" className="modal-input" placeholder="Monto" value={paymentAmount} onChange={e => setPaymentAmount(e.target.value)} />
                                    <textarea className="modal-textarea" placeholder="Nota" value={statusNotes} onChange={e => setStatusNotes(e.target.value)} />
                                    <div className="modal-actions">
                                        <button onClick={handleAddPayment} className="modal-btn-primary">Guardar</button>
                                        <button onClick={closeModal} className="modal-btn-secondary">Cancelar</button>
                                    </div>
                                </>
                            )}
                            {modalType === 'status' && (
                                <>
                                    <label>Estado:</label>
                                    <select className="modal-select" value={newStatus} onChange={e => setNewStatus(e.target.value)}>
                                        <option value="pendiente">Pendiente</option>
                                        <option value="en_preparacion">Producción</option>
                                        <option value="listo">Listo</option>
                                        <option value="entregado">Entregado</option>
                                        <option value="cancelado">Cancelado</option>
                                    </select>
                                    <input type="date" className="modal-input" value={newDeliveryDate} onChange={e => setNewDeliveryDate(e.target.value)} />
                                    <textarea className="modal-textarea" placeholder="Nota" value={statusNotes} onChange={e => setStatusNotes(e.target.value)} />
                                    <div className="modal-actions">
                                        <button onClick={handleUpdateStatus} className="modal-btn-primary">Actualizar</button>
                                        <button onClick={closeModal} className="modal-btn-secondary">Cancelar</button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
            {showTicket && ticketData && <SaleTicket sale={ticketData} onClose={() => setShowTicket(false)} />}
            <CreateOrderModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onOrderCreated={() => { loadOrders(); loadStats(); }}
                showToast={showToast}
            />
        </div>
    );
};

export default OrdersManagementPage;